/**
 * OVERRIDE Navigation.tsx
 * FILE: @kitconcept/volto-light-theme/src/components/Navigation/Navigation.tsx
 * DATE: 2026-06-26
 *
 * CHANGES vs. upstream:
 *
 * 1. Per-item fat menu (matches the project's conditional fat-menu rule)
 *    The fat menu must only open when a top-level navigation item actually
 *    has children visible in the navigation. If a parent page has no
 *    children the top-level item must link directly to the page instead
 *    of opening an (almost empty) fat-menu drawer.
 *    -> Replaced the global `hasFatMenu` branch inside `items.map` with a
 *       per-item predicate `itemHasFatMenu = hasFatMenu && item.items.length > 0`.
 *       When the predicate is false we fall back to <NavItem> (direct NavLink).
 *
 * 2. Synthetic "Overview" subitem
 *    Users often miss that the .submenu-header also links to the parent
 *    page, because it reads as decorative copy. We prepend an explicit
 *    "Overview" entry as the first subitem in the drawer so the parent
 *    page is reachable as an unambiguous list item.
 *    Label is i18n'd via `messages.overview`.
 *
 * 3. Heading kept for a11y, hidden visually
 *    The original .submenu-header was a <NavLink> around an <h2>, both
 *    pointing at the parent page. With the Overview subitem now serving
 *    navigation, keeping the link would create a duplicate target and an
 *    extra tab stop. We:
 *      - drop the surrounding <NavLink> (Overview is the navigation)
 *      - keep the bare <h2 className="submenu-header"> in the DOM so
 *        screen-reader users can still navigate the drawer by heading
 *      - hide it visually via the .submenu-header rule in
 *        theme/components/_navigation.scss (visually-hidden pattern)
 *
 * 4. Auto-close on route change
 *    Clicking a top-level item without children renders as a direct
 *    NavLink (see change #1). That click happens inside <nav>, so the
 *    existing outside-click listener does not catch it and the previously
 *    opened drawer stays visible after navigation. Added a route-change
 *    effect that clears the open state, also covering browser back/forward
 *    and programmatic navigation.
 *
 * Everything else is identical to the upstream file.
 */

import { useState, useEffect, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { NavLink } from 'react-router-dom';
import doesNodeContainClick from '@kitconcept/volto-light-theme/helpers/doesNodeContainClick';
import { useIntl, defineMessages } from 'react-intl';
import cx from 'classnames';
import { getBaseUrl } from '@plone/volto/helpers/Url/Url';
import { hasApiExpander } from '@plone/volto/helpers/Utils/Utils';
import config from '@plone/volto/registry';

import { getNavigation } from '@plone/volto/actions/navigation/navigation';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import clearSVG from '@plone/volto/icons/clear.svg';
import NavItem from '@plone/volto/components/theme/Navigation/NavItem';

const messages = defineMessages({
  closeMenu: {
    id: 'Close menu',
    defaultMessage: 'Close menu',
  },
  openFatMenu: {
    id: 'Open menu',
    defaultMessage: 'Open menu',
  },
  overview: {
    id: 'Overview',
    defaultMessage: 'Overview',
  },
});

const Navigation = ({ pathname }) => {
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(null);
  const [currentOpenIndex, setCurrentOpenIndex] = useState(null);
  const navigation = useRef(null);
  const dispatch = useDispatch();
  const intl = useIntl();
  const headerSettings = useSelector(
    (state) =>
      state.content.data?.['@components']?.inherit?.['voltolighttheme.header']
        ?.data,
  );
  const formData = useSelector((state) => state.form.global);

  const hasFatMenuSetting =
    !isEmpty(formData) && formData?.has_fat_menu !== undefined
      ? formData.has_fat_menu
      : headerSettings?.has_fat_menu;
  const hasFatMenu = hasFatMenuSetting ?? false;

  const lang = useSelector((state) => state.intl.locale);
  const token = useSelector((state) => state.userSession.token, shallowEqual);
  const items = useSelector((state) => state.navigation.items, shallowEqual);

  const closeMenu = () => {
    setDesktopMenuOpen(null);
    setCurrentOpenIndex(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navigation.current && doesNodeContainClick(navigation.current, event))
        return;
      closeMenu();
    };

    document.addEventListener('mousedown', handleClickOutside, false);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, false);
    };
  }, []);

  useEffect(() => {
    if (!hasApiExpander('navigation', getBaseUrl(pathname))) {
      dispatch(getNavigation(getBaseUrl(pathname), config.settings.navDepth));
    }
  }, [pathname, token, dispatch]);

  // Close any open fat-menu drawer on route change. useState setters are
  // stable, so `closeMenu` is intentionally not in deps (exhaustive-deps
  // would force it and break the effect on every render).
  useEffect(() => {
    setDesktopMenuOpen(null);
    setCurrentOpenIndex(null);
  }, [pathname]);

  const isActive = (url) => {
    return (url === '' && pathname === '/') || (url !== '' && pathname === url);
  };

  const openMenu = (index) => {
    if (index === currentOpenIndex) {
      setDesktopMenuOpen(null);
      setCurrentOpenIndex(null);
    } else {
      setDesktopMenuOpen(index);
      setCurrentOpenIndex(index);
    }
  };

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape' || event.keyCode === 27) {
        closeMenu();
      }
    };
    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, []);

  return (
    <nav
      id="navigation"
      aria-label="navigation"
      className="navigation"
      ref={navigation}
    >
      <div className={'computer large screen widescreen only'}>
        <ul className="desktop-menu">
          {items.map((item, index) => {
            // Fat menu is only meaningful when this top-level item actually
            // has children visible in the navigation. Without children the
            // drawer would just repeat the parent link, forcing a useless
            // second click - so we render a direct NavLink instead.
            const itemHasFatMenu = hasFatMenu && (item.items?.length ?? 0) > 0;

            return (
              <li key={item.url}>
                {itemHasFatMenu ? (
                  <>
                    <button
                      onClick={() => openMenu(index)}
                      className={cx('item', {
                        active:
                          desktopMenuOpen === index ||
                          (!desktopMenuOpen && pathname === item.url),
                      })}
                      aria-label={intl.formatMessage(messages.openFatMenu)}
                      aria-expanded={desktopMenuOpen === index}
                    >
                      {item.title}
                    </button>

                    <div className="submenu-wrapper">
                      <div
                        className={cx('submenu', {
                          active: desktopMenuOpen === index,
                        })}
                      >
                        <div className="submenu-inner">
                          {/*
                            Visually-hidden heading.
                            Kept in the DOM so screen-reader users can still
                            navigate the drawer by heading and understand
                            which section the list belongs to. Not displayed,
                            because the synthetic "Overview" subitem below is
                            now the unambiguous visible link to the parent
                            page; rendering a styled heading would add a
                            redundant tab stop and a duplicate link target.
                          */}
                          <h2 className="submenu-header">
                            {item.nav_title ?? item.title}
                          </h2>
                          <button
                            className="close"
                            onClick={closeMenu}
                            aria-label={intl.formatMessage(messages.closeMenu)}
                          >
                            <Icon name={clearSVG} size="48px" />
                          </button>
                          <ul>
                            {/*
                              Synthetic "Overview" item: the upstream
                              .submenu-header looks like decorative copy, so
                              users miss that it links to the parent page.
                              We prepend an explicit "Overview" menu entry
                              pointing to the same URL so the parent page is
                              reachable as an unambiguous list item.
                            */}
                            <li
                              className="subitem-wrapper subitem-overview"
                              key="__overview__"
                            >
                              <NavLink
                                to={item.url === '' ? '/' : item.url}
                                onClick={() => closeMenu()}
                                className={cx({
                                  current: isActive(item.url),
                                })}
                              >
                                <span className="left-arrow">&#8212;</span>
                                <span>
                                  {intl.formatMessage(messages.overview)}
                                </span>
                              </NavLink>
                            </li>
                            {item.items &&
                              item.items.length > 0 &&
                              item.items.map((subitem) => (
                                <li
                                  className="subitem-wrapper"
                                  key={subitem.url}
                                >
                                  <NavLink
                                    to={subitem.url}
                                    onClick={() => closeMenu()}
                                    className={cx({
                                      current: isActive(subitem.url),
                                    })}
                                  >
                                    <span className="left-arrow">&#8212;</span>
                                    <span>
                                      {subitem.nav_title || subitem.title}
                                    </span>
                                  </NavLink>
                                  <div className="sub-submenu">
                                    <ul>
                                      {subitem.items &&
                                        subitem.items.length > 0 &&
                                        subitem.items.map((subsubitem) => (
                                          <li
                                            className="subsubitem-wrapper"
                                            key={subsubitem.url}
                                          >
                                            <NavLink
                                              to={subsubitem.url}
                                              onClick={() => closeMenu()}
                                              className={cx({
                                                current: isActive(
                                                  subsubitem.url,
                                                ),
                                              })}
                                            >
                                              <span className="left-arrow">
                                                &#8212;
                                              </span>
                                              <span>
                                                {subsubitem.nav_title ||
                                                  subsubitem.title}
                                              </span>
                                            </NavLink>
                                          </li>
                                        ))}
                                    </ul>
                                  </div>
                                </li>
                              ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <NavItem item={item} lang={lang} key={item.url} />
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
};

export default Navigation;
